import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Camera, Upload, X, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react'

interface ScanResult {
  text: string
  format: string
  timestamp: Date
}

interface ScannerProps {
  onScan: (result: ScanResult) => void
  onError?: (error: string) => void
  className?: string
}

export function Scanner({ onScan, onError, className }: ScannerProps) {
  const [isScanning, setIsScanning] = useState(false)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [scanHistory, setScanHistory] = useState<ScanResult[]>([])

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Check for camera permissions
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const permissions = await navigator.permissions.query({ name: 'camera' as PermissionName })
        setHasPermission(permissions.state === 'granted')
      } catch {
        // Fallback for browsers that don't support permissions API
        setHasPermission(null)
      }
    }
    checkPermissions()
  }, [])

  // Start camera stream
  const startCamera = useCallback(async () => {
    try {
      setError(null)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      })
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        streamRef.current = stream
        setIsScanning(true)
        setHasPermission(true)
        startScanning()
      }
    } catch (err: any) {
      const errorMessage = err.name === 'NotAllowedError' 
        ? 'Camera permission denied. Please allow camera access to scan codes.'
        : err.name === 'NotFoundError'
        ? 'No camera found. Please connect a camera to scan codes.'
        : 'Failed to access camera. Please try again.'
      
      setError(errorMessage)
      onError?.(errorMessage)
    }
  }, [onError])

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }
    setIsScanning(false)
  }, [])

  // Start scanning loop
  const startScanning = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return

    const scanFrame = () => {
      if (!videoRef.current || !canvasRef.current || !isScanning) return

      const video = videoRef.current
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      
      if (!ctx) return

      // Set canvas size to match video
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      
      // Draw current video frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      
      // Get image data for scanning
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      
      // Try to detect barcodes/QR codes
      try {
        // This is a simplified implementation
        // In a real app, you'd use a library like @zxing/browser or jsQR
        const result = detectCode(imageData)
        if (result) {
          const scanResult: ScanResult = {
            text: result.text,
            format: result.format,
            timestamp: new Date()
          }
          
          setScanHistory(prev => [scanResult, ...prev.slice(0, 9)]) // Keep last 10
          onScan(scanResult)
          stopCamera()
        }
      } catch (err) {
        console.warn('Scan error:', err)
      }
    }

    // Scan every 100ms
    scanIntervalRef.current = setInterval(scanFrame, 100)
  }, [isScanning, onScan, stopCamera])

  // Simplified code detection (replace with real library)
  const detectCode = (_imageData: ImageData): { text: string; format: string } | null => {
    // This is a placeholder - in reality you'd use jsQR or @zxing/browser
    // For demo purposes, we'll simulate detection
    return null
  }

  // Handle file upload
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const result = detectCode(imageData)
        
        if (result) {
          const scanResult: ScanResult = {
            text: result.text,
            format: result.format,
            timestamp: new Date()
          }
          setScanHistory(prev => [scanResult, ...prev.slice(0, 9)])
          onScan(scanResult)
        } else {
          setError('No barcode or QR code found in image')
        }
      }
      img.src = e.target?.result as string
    }
    reader.readAsDataURL(file)
  }, [onScan])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera()
    }
  }, [stopCamera])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Scanner Interface */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.8), rgba(36, 41, 56, 0.8))',
        border: '2px solid rgba(0, 240, 255, 0.3)',
        borderRadius: '12px',
        padding: '20px',
        fontFamily: 'Rajdhani, sans-serif'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '1px solid rgba(0, 240, 255, 0.2)'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: 'rgba(0, 240, 255, 0.2)',
            border: '2px solid #00F0FF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Camera style={{ width: 20, height: 20, color: '#00F0FF' }} />
          </div>
          <div>
            <h3 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#00F0FF',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: 0
            }}>
              QR/BARCODE SCANNER
            </h3>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '12px',
              color: '#8A95AA',
              margin: '4px 0 0 0'
            }}>
              Use your camera or upload an image to scan codes
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Camera View */}
          <div style={{
            position: 'relative',
            aspectRatio: '16/9',
            background: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '2px solid rgba(0, 240, 255, 0.3)'
          }}>
            {isScanning ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <Camera style={{ width: 64, height: 64, color: '#00F0FF', opacity: 0.5, margin: '0 auto 16px' }} />
                  <p style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    color: '#8A95AA',
                    margin: 0
                  }}>
                    Camera ready
                  </p>
                </div>
              </div>
            )}

            {/* Scanning overlay */}
            {isScanning && (
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '192px',
                  height: '192px',
                  border: '2px dashed #00F0FF',
                  borderRadius: '8px',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }} />
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '2px solid rgba(239, 68, 68, 0.4)',
              borderRadius: '8px'
            }}>
              <AlertCircle style={{ width: 16, height: 16, color: '#EF4444', flexShrink: 0 }} />
              <p style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                color: '#EF4444',
                margin: 0
              }}>
                {error}
              </p>
            </div>
          )}

          {/* Permission Status */}
          {hasPermission === false && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              background: 'rgba(255, 217, 61, 0.1)',
              border: '2px solid rgba(255, 217, 61, 0.4)',
              borderRadius: '8px'
            }}>
              <AlertCircle style={{ width: 16, height: 16, color: '#FFD93D', flexShrink: 0 }} />
              <p style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                color: '#FFD93D',
                margin: 0
              }}>
                Camera access is blocked. Please enable camera permissions in your browser settings.
              </p>
            </div>
          )}

          {/* Controls */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {!isScanning ? (
              <>
                <button
                  onClick={startCamera}
                  disabled={hasPermission === false}
                  style={{
                    flex: 1,
                    background: 'rgba(0, 240, 255, 0.2)',
                    border: '2px solid #00F0FF',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    color: '#00F0FF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: hasPermission === false ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: hasPermission === false ? 0.5 : 1,
                    boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
                  }}
                >
                  <Camera style={{ width: 16, height: 16 }} />
                  {hasPermission === false ? 'CAMERA BLOCKED' : 'START CAMERA'}
                </button>
                <button
                  onClick={() => document.getElementById('file-upload')?.click()}
                  style={{
                    flex: 1,
                    background: 'rgba(185, 27, 255, 0.1)',
                    border: '2px solid rgba(185, 27, 255, 0.4)',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    color: '#B91BFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Upload style={{ width: 16, height: 16 }} />
                  UPLOAD IMAGE
                </button>
              </>
            ) : (
              <button
                onClick={stopCamera}
                style={{
                  flex: 1,
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '2px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '6px',
                  padding: '12px 20px',
                  color: '#EF4444',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '14px',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <X style={{ width: 16, height: 16 }} />
                STOP CAMERA
              </button>
            )}
          </div>

          {/* Hidden file input */}
          <input
            id="file-upload"
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />

          {/* Hidden canvas for scanning */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      </div>

      {/* Scan History */}
      {scanHistory.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.8), rgba(36, 41, 56, 0.8))',
          border: '2px solid rgba(185, 27, 255, 0.3)',
          borderRadius: '12px',
          padding: '20px',
          fontFamily: 'Rajdhani, sans-serif'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '1px solid rgba(185, 27, 255, 0.2)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'rgba(185, 27, 255, 0.2)',
              border: '2px solid #B91BFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <RefreshCw style={{ width: 20, height: 20, color: '#B91BFF' }} />
            </div>
            <div>
              <h3 style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '20px',
                fontWeight: 700,
                color: '#B91BFF',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                margin: 0
              }}>
                RECENT SCANS
              </h3>
              <p style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '12px',
                color: '#8A95AA',
                margin: '4px 0 0 0'
              }}>
                Your recent scan results
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {scanHistory.map((result, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  background: 'rgba(0, 0, 0, 0.4)',
                  borderRadius: '8px',
                  border: '1px solid rgba(185, 27, 255, 0.2)'
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    color: '#FFFFFF',
                    margin: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {result.text}
                  </p>
                  <p style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '12px',
                    color: '#8A95AA',
                    margin: '4px 0 0 0'
                  }}>
                    {result.format} • {result.timestamp.toLocaleTimeString()}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '16px' }}>
                  <span style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#B91BFF',
                    background: 'rgba(185, 27, 255, 0.2)',
                    border: '1px solid rgba(185, 27, 255, 0.3)',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    {result.format}
                  </span>
                  <button
                    onClick={() => onScan(result)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#6BCF7F',
                      cursor: 'pointer',
                      padding: '4px'
                    }}
                  >
                    <CheckCircle style={{ width: 16, height: 16 }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
